/**
 * Constantes de roles de usuario.
 * Se mantienen en sync con el enum del backend (User.role).
 */
export const ROLES = Object.freeze({
  BOCHAS_ADMIN: "BOCHAS_ADMIN",
  Asociación_ADMIN: "Asociación_ADMIN", // ex Federación_ADMIN (2026-07-16)
  CLUB_ADMIN: "CLUB_ADMIN",
  USER_ROLE: "USER_ROLE", // usuario sin permisos de admin (default legacy)
});

/**
 * Devuelve true si el user tiene exactamente el rol indicado.
 */
export function hasRole(user, role) {
  return Boolean(user && user.role === role);
}

/**
 * Devuelve true si el user tiene ALGUNO de los roles indicados.
 */
export function hasAnyRole(user, ...roles) {
  if (!user || !user.role) return false;
  return roles.includes(user.role);
}

/**
 * Devuelve true si el user es BOCHAS_ADMIN (admin provincial).
 */
export function isBochasAdmin(user) {
  return hasRole(user, ROLES.BOCHAS_ADMIN);
}

/**
 * Devuelve true si el user es Asociación_ADMIN (ex Federación_ADMIN).
 */
export function isAsociacionAdmin(user) {
  return hasRole(user, ROLES.Asociación_ADMIN);
}

/**
 * Devuelve true si el user es CLUB_ADMIN.
 */
export function isClubAdmin(user) {
  return hasRole(user, ROLES.CLUB_ADMIN);
}

/**
 * Devuelve true si el user tiene acceso al panel de admin
 * (cualquiera de los 3 roles válidos).
 */
export function isAdminUser(user) {
  return hasAnyRole(user, ROLES.BOCHAS_ADMIN, ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN);
}

/**
 * Devuelve true si el user es desarrollador de la plataforma.
 *
 * El flag `isDeveloper` es independiente del rol: un dev bypasea cualquier
 * check de permisos. Reservado a cuentas de los dueños del proyecto.
 * Se setea en la DB (`user.is_developer = true`); NO se cambia desde la UI.
 *
 * Si un user es dev, también se considera admin (puede entrar al panel).
 */
export function isDeveloper(user) {
  return Boolean(user && user.isDeveloper === true);
}

export function isGeneralAdmin(user) {
  return isDeveloper(user) && hasRole(user, ROLES.BOCHAS_ADMIN);
}

/**
 * Devuelve true si el user puede ver/editar federaciones y clubes
 * (dev o BOCHAS_ADMIN — ambos con distintos niveles de permiso).
 */
export function canManageInstitutional(user) {
  return isDeveloper(user) || hasRole(user, ROLES.BOCHAS_ADMIN);
}

/**
 * Etiqueta legible para mostrar el rol en UI.
 */
export function roleLabel(role, user = null) {
  switch (role) {
    case ROLES.BOCHAS_ADMIN:
      return isGeneralAdmin(user)
        ? "Administrador general"
        : "Administrador provincial";
    case ROLES.Asociación_ADMIN:
      return "Admin de asociación";
    case ROLES.CLUB_ADMIN:
      return "Admin de club";
    default:
      return "Sin rol";
  }
}
